import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
} from '@mui/material';

type DialogKind = 'alert' | 'confirm';

type DialogState =
    | {
          open: false;
      }
    | {
          open: true;
          kind: DialogKind;
          title?: string;
          message: string;
          confirmText?: string;
          cancelText?: string;
          resolve?: (result: boolean) => void;
      };

type AppDialogApi = {
    alert: (message: string, options?: { title?: string; confirmText?: string }) => Promise<void>;
    confirm: (
        message: string,
        options?: { title?: string; confirmText?: string; cancelText?: string }
    ) => Promise<boolean>;
};

const AppDialogContext = createContext<AppDialogApi | null>(null);

export const useAppDialog = () => {
    const ctx = useContext(AppDialogContext);
    if (!ctx) {
        throw new Error('useAppDialog must be used within <AppDialogProvider>.');
    }
    return ctx;
};

export const AppDialogProvider = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<DialogState>({ open: false });

    const close = useCallback(() => {
        setState({ open: false });
    }, []);

    const alert = useCallback(
        (message: string, options?: { title?: string; confirmText?: string }) => {
            return new Promise<void>((resolve) => {
                setState({
                    open: true,
                    kind: 'alert',
                    title: options?.title,
                    message,
                    confirmText: options?.confirmText,
                    resolve: () => resolve(),
                });
            });
        },
        []
    );

    const confirm = useCallback(
        (
            message: string,
            options?: { title?: string; confirmText?: string; cancelText?: string }
        ) => {
            return new Promise<boolean>((resolve) => {
                setState({
                    open: true,
                    kind: 'confirm',
                    title: options?.title,
                    message,
                    confirmText: options?.confirmText,
                    cancelText: options?.cancelText,
                    resolve,
                });
            });
        },
        []
    );

    const value = useMemo<AppDialogApi>(() => ({ alert, confirm }), [alert, confirm]);

    const AppDialog = (
        <Dialog
            open={state.open}
            onClose={() => {
                if (state.open && state.resolve) state.resolve(false);
                close();
            }}
            maxWidth="xs"
            fullWidth
        >
            {state.open && state.title && <DialogTitle>{state.title}</DialogTitle>}
            {state.open && (
                <DialogContent>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{state.message}</Typography>
                </DialogContent>
            )}
            {state.open && (
                <DialogActions>
                    {state.kind === 'confirm' && (
                        <Button
                            onClick={() => {
                                if (state.resolve) state.resolve(false);
                                close();
                            }}
                        >
                            {state.cancelText ?? 'Cancel'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (state.resolve) state.resolve(true);
                            close();
                        }}
                    >
                        {state.confirmText ?? 'OK'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );

    return (
        <AppDialogContext.Provider value={value}>
            {children}
            {AppDialog}
        </AppDialogContext.Provider>
    );
};
